// StoreExtensionsEditor — the `extensions` field of a ProductContentType: a
// checklist of the store-enabled product extensions (store {product_model}),
// in the store's order (STORE_PRODUCT_MODEL). The ticked ids are the
// extensions a product of the type is made of — its admin tabs, its checkout
// hooks, its storefront slots. Nothing ticked means every enabled extension.
//
// The labels are the admin registry's (aldoo-ra/Admin/Store/extensions), the
// server's otherwise. The registry is loaded on demand: it composes the
// StoreProduct CMS type, which registers its own field editors here, so a
// static import would make this resolver import itself.
import React, { useEffect, useState } from "react"
import Checkbox from "aldoo-ra/Checkbox"
import Typography from "aldoo-ra/Typography"
import { storeProductModel } from "aldoo-ra/Store/api"
import { OPTIONAL_BUILTIN_TABS, OPTIONAL_BUILTIN_TAB_IDS } from "aldoo-ra/Admin/Store/extensions/builtin-tabs"

const asIds = (value) => (Array.isArray(value) ? value.filter((id) => typeof id === "string" && id) : [])

export default function StoreExtensionsChecklist({ value, onChange, readOnly = false }) {
  const [model, setModel] = useState(null)
  const [registry, setRegistry] = useState(null)
  const [error, setError] = useState(null)
  const all = asIds(value)
  // the list names the built-in tabs a type shows too (Details, Licensing, Metadata)
  const tickedTabs = all.filter((id) => OPTIONAL_BUILTIN_TAB_IDS.includes(id))
  const ticked = all.filter((id) => !OPTIONAL_BUILTIN_TAB_IDS.includes(id))

  useEffect(() => {
    let alive = true
    storeProductModel()
      .then((res) => {
        if (!alive) return
        if (res?.error) throw new Error(res.error)
        setModel({ extensions: res?.extensions || [], enabled: res?.enabled || [] })
      })
      .catch((e) => alive && setError(e?.message || "Failed to load the store extensions"))
    import("aldoo-ra/Admin/Store/extensions")
      .then((module) => alive && setRegistry(module))
      .catch(() => {
        // the server's labels do
      })
    return () => {
      alive = false
    }
  }, [])

  if (error) return <Typography variant="admin_body2" className="text-admin_error">{error}</Typography>
  if (!model) return <Typography variant="admin_body2" className="opacity-70">Loading the store extensions…</Typography>

  const serverEntry = (id) => model.extensions.find((entry) => entry.id === id)
  const labelOf = (id) => registry?.getStoreProductExtension?.(id)?.label || serverEntry(id)?.label || id
  const descriptionOf = (id) => registry?.getStoreProductExtension?.(id)?.description || serverEntry(id)?.description || ""
  // what the type ticked but the store no longer enables stays listed, so it can be unticked
  const stale = ticked.filter((id) => !model.enabled.includes(id))
  // an extension every product has (`allProducts`) is not the type's to pick
  const rows = [...model.enabled, ...stale].filter((id) => !registry?.getStoreProductExtension?.(id)?.allProducts)

  const toggle = (id) => {
    if (readOnly) return
    const next = ticked.includes(id) ? ticked.filter((entry) => entry !== id) : [...ticked, id]
    // kept in the store's order, whatever order they were ticked in; the tabs stay as they are
    onChange?.([...tickedTabs, ...rows.filter((entry) => next.includes(entry))])
  }
  const toggleTab = (id) => {
    if (readOnly) return
    // an empty list shows every tab: unticking one starts from all of them ticked
    const current = all.length ? tickedTabs : OPTIONAL_BUILTIN_TAB_IDS
    const next = current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id]
    onChange?.([...OPTIONAL_BUILTIN_TAB_IDS.filter((entry) => next.includes(entry)), ...ticked])
  }

  if (!rows.length) {
    return (
      <Typography variant="admin_body2" className="opacity-70">
        No store product extension is enabled (Store → Product Model).
      </Typography>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {/* the editor's own tabs a type may go without */}
      <Typography variant="admin_body1" className="font-semibold mt-1">
        Built-in tabs
      </Typography>
      <Typography variant="admin_body2" className="opacity-70">
        {all.length ? "Pricing and Availability are always shown; the others only when ticked." : "Nothing ticked: products of this type show every built-in tab. Pricing and Availability are always shown."}
      </Typography>
      <div className="flex flex-col divide-y divide-admin_bordered border border-admin_bordered rounded-lg mb-3" data-testid="builtin-tabs">
        {OPTIONAL_BUILTIN_TABS.map((tab) => (
          <div key={tab.id} className="flex items-start gap-3 p-3">
            <Checkbox isOn={!all.length || tickedTabs.includes(tab.id)} onChange={() => toggleTab(tab.id)} disabled={readOnly} />
            <div className="flex flex-col cursor-pointer min-w-0" onClick={() => toggleTab(tab.id)}>
              <Typography variant="admin_body1" className="font-medium">
                {tab.label}
              </Typography>
              <Typography variant="admin_body2" className="opacity-70">
                {tab.description}
              </Typography>
            </div>
          </div>
        ))}
      </div>
      <Typography variant="admin_body1" className="font-semibold">
        Extensions
      </Typography>
      <Typography variant="admin_body2" className="opacity-70">
        {ticked.length ? `${ticked.length} of ${model.enabled.length} enabled extensions` : "None ticked: products of this type use every enabled extension"}
      </Typography>
      <div className="flex flex-col divide-y divide-admin_bordered border border-admin_bordered rounded-lg">
        {rows.map((id) => (
          <div key={id} className="flex items-start gap-3 p-3">
            <Checkbox isOn={ticked.includes(id)} onChange={() => toggle(id)} disabled={readOnly} />
            <div className="flex flex-col cursor-pointer min-w-0" onClick={() => toggle(id)}>
              <Typography variant="admin_body1" className="font-medium">
                {labelOf(id)}
                {stale.includes(id) && <span className="ml-2 text-admin_error text-xs font-normal">not enabled</span>}
              </Typography>
              {descriptionOf(id) && (
                <Typography variant="admin_body2" className="opacity-70">
                  {descriptionOf(id)}
                </Typography>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
